/**
 * ReportDailyPage - Página de Reporte Diario
 * Muestra cheques recibidos, pendientes y depositados en una fecha específica
 */
import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Button,
  TextField,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
} from '@mui/material';
import {
  FileDownload as FileDownloadIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from '@mui/icons-material';
import { PageHeader, LoadingSpinner } from '../components/common';
import { useNotification } from '../context/NotificationContext';
import reporteService from '../services/reporteService';
import { parseApiDate } from '../utils/formatters';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

const ReportDailyPage = () => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const { showError, showSuccess } = useNotification();

  const loadReport = async () => {
    try {
      setLoading(true);
      const response = await reporteService.getDailyReport(selectedDate);
      const data = response.data || response;

      // Mapear los datos del backend (snake_case) a formato esperado
      const mappedData = {
        fecha: data.fecha,
        chequesRecibidos: data.cheques_recibidos || [],
        porDepositar: data.cheques_por_depositar || [],
        depositados: data.depositos_realizados || [],
        totales: data.totales || {},
        totalesPorCajero: data.totales_por_cajero || [],
        totalesPorBanco: data.totales_por_banco || [],
      };

      setReportData(mappedData);
    } catch (error) {
      console.error('Error al cargar reporte:', error);
      showError('Error al cargar el reporte diario');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, []);

  const handleGenerateReport = () => {
    loadReport();
  };

  const handleExportExcel = async () => {
    try {
      const blob = await reporteService.exportReportToExcel('diario', {
        fecha: selectedDate,
      });
      reporteService.downloadReport(blob, `reporte-diario-${selectedDate}.xlsx`);
      showSuccess('Reporte exportado a Excel correctamente');
    } catch (error) {
      console.error('Error al exportar Excel:', error);
      showError('Error al exportar el reporte a Excel');
    }
  };

  const handleExportPDF = async () => {
    try {
      const blob = await reporteService.exportReportToPDF('diario', {
        fecha: selectedDate,
      });
      reporteService.downloadReport(blob, `reporte-diario-${selectedDate}.pdf`);
      showSuccess('Reporte exportado a PDF correctamente');
    } catch (error) {
      console.error('Error al exportar PDF:', error);
      showError('Error al exportar el reporte a PDF');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
    }).format(value || 0);
  };

  // Formatear una fecha del API (DATEONLY) sin desfase de zona horaria
  const formatFecha = (fecha) => {
    const parsed = parseApiDate(fecha);
    return parsed ? parsed.toLocaleDateString('es-AR') : '-';
  };

  // Preparar datos para el gráfico de torta
  const prepareChartData = () => {
    if (!reportData?.totalesPorCajero) return [];

    return reportData.totalesPorCajero.map((cajero) => ({
      name: cajero.cajero || 'Sin cajero',
      value: cajero.totalMonto || 0,
      cantidad: cajero.totalCheques || 0,
    }));
  };

  if (loading && !reportData) {
    return <LoadingSpinner message="Cargando reporte diario..." />;
  }

  return (
    <Box>
      <PageHeader
        title="Reporte Diario"
        subtitle="Resumen de cheques recibidos, pendientes y depositados del día"
        breadcrumbs={[
          { label: 'Inicio', path: '/dashboard' },
          { label: 'Reportes', path: '/reportes' },
          { label: 'Reporte Diario' },
        ]}
      />

      {/* Filtros y Acciones */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              label="Fecha"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                onClick={handleGenerateReport}
                disabled={loading}
              >
                Generar Reporte
              </Button>
              <Button
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={handleExportExcel}
                disabled={!reportData || loading}
              >
                Exportar Excel
              </Button>
              <Button
                variant="outlined"
                startIcon={<PictureAsPdfIcon />}
                onClick={handleExportPDF}
                disabled={!reportData || loading}
              >
                Exportar PDF
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {reportData && (
        <>
          {/* Resumen General */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Cheques Recibidos
                  </Typography>
                  <Typography variant="h4">
                    {reportData.chequesRecibidos?.length || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {formatCurrency(
                      reportData.chequesRecibidos?.reduce((sum, ch) => {
                        const monto = parseFloat(ch.monto);
                        return sum + (isNaN(monto) ? 0 : monto);
                      }, 0) || 0
                    )}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Por Depositar Hoy
                  </Typography>
                  <Typography variant="h4">
                    {reportData.porDepositar?.length || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {formatCurrency(
                      reportData.porDepositar?.reduce((sum, ch) => {
                        const monto = parseFloat(ch.monto);
                        return sum + (isNaN(monto) ? 0 : monto);
                      }, 0) || 0
                    )}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Depositados Hoy
                  </Typography>
                  <Typography variant="h4">
                    {reportData.depositados?.length || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {formatCurrency(
                      reportData.depositados?.reduce((sum, dep) => {
                        const monto = parseFloat(dep.monto);
                        return sum + (isNaN(monto) ? 0 : monto);
                      }, 0) || 0
                    )}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Tabs con Detalles */}
          <Paper sx={{ mb: 3 }}>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
              <Tab label="Cheques Recibidos" />
              <Tab label="Por Depositar" />
              <Tab label="Depositados" />
            </Tabs>

            <Box sx={{ p: 3 }}>
              {/* Tab: Cheques Recibidos */}
              {activeTab === 0 && (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Número</TableCell>
                        <TableCell>Cliente</TableCell>
                        <TableCell>Banco</TableCell>
                        <TableCell>Cajero</TableCell>
                        <TableCell align="right">Monto</TableCell>
                        <TableCell>Fecha Pago</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reportData.chequesRecibidos?.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            No hay cheques recibidos en esta fecha
                          </TableCell>
                        </TableRow>
                      ) : (
                        reportData.chequesRecibidos?.map((cheque) => (
                          <TableRow key={cheque.id}>
                            <TableCell>{cheque.numero_cheque}</TableCell>
                            <TableCell>{cheque.cliente?.nombre || '-'}</TableCell>
                            <TableCell>{cheque.banco?.nombre || '-'}</TableCell>
                            <TableCell>{cheque.cajero?.nombre || '-'}</TableCell>
                            <TableCell align="right">{formatCurrency(cheque.monto)}</TableCell>
                            <TableCell>
                              {formatFecha(cheque.fecha_pago)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Tab: Por Depositar */}
              {activeTab === 1 && (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Número</TableCell>
                        <TableCell>Cliente</TableCell>
                        <TableCell>Banco</TableCell>
                        <TableCell align="right">Monto</TableCell>
                        <TableCell>Fecha Pago</TableCell>
                        <TableCell>Estado</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reportData.porDepositar?.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            No hay cheques pendientes para depositar hoy
                          </TableCell>
                        </TableRow>
                      ) : (
                        reportData.porDepositar?.map((cheque) => (
                          <TableRow key={cheque.id}>
                            <TableCell>{cheque.numero_cheque}</TableCell>
                            <TableCell>{cheque.cliente?.nombre || '-'}</TableCell>
                            <TableCell>{cheque.banco?.nombre || '-'}</TableCell>
                            <TableCell align="right">{formatCurrency(cheque.monto)}</TableCell>
                            <TableCell>
                              {formatFecha(cheque.fecha_pago)}
                            </TableCell>
                            <TableCell>{cheque.estado}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Tab: Depositados */}
              {activeTab === 2 && (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Cuenta Bancaria</TableCell>
                        <TableCell>Banco</TableCell>
                        <TableCell align="right"># Cheques</TableCell>
                        <TableCell align="right">Monto Total</TableCell>
                        <TableCell>Fecha Depósito</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reportData.depositados?.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            No hay depósitos realizados en esta fecha
                          </TableCell>
                        </TableRow>
                      ) : (
                        reportData.depositados?.map((deposito) => (
                          <TableRow key={deposito.id}>
                            <TableCell>
                              {deposito.cuenta_bancaria?.numero_cuenta || '-'}
                            </TableCell>
                            <TableCell>
                              {deposito.cuenta_bancaria?.banco?.nombre || '-'}
                            </TableCell>
                            <TableCell align="right">{deposito.cheques?.length || 0}</TableCell>
                            <TableCell align="right">{formatCurrency(deposito.monto_total)}</TableCell>
                            <TableCell>
                              {formatFecha(deposito.fecha_deposito)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Paper>

          {/* Totales y Gráfico */}
          <Grid container spacing={3}>
            {/* Totales por Cajero */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Totales por Cajero
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Cajero</TableCell>
                        <TableCell align="right"># Cheques</TableCell>
                        <TableCell align="right">Monto Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reportData.totalesPorCajero?.map((cajero, index) => (
                        <TableRow key={index}>
                          <TableCell>{cajero.cajero || 'Sin cajero'}</TableCell>
                          <TableCell align="right">{cajero.totalCheques}</TableCell>
                          <TableCell align="right">
                            {formatCurrency(cajero.totalMonto)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>

            {/* Totales por Banco */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Totales por Banco
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Banco</TableCell>
                        <TableCell align="right"># Cheques</TableCell>
                        <TableCell align="right">Monto Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reportData.totalesPorBanco?.map((banco, index) => (
                        <TableRow key={index}>
                          <TableCell>{banco.banco || 'Sin banco'}</TableCell>
                          <TableCell align="right">{banco.totalCheques}</TableCell>
                          <TableCell align="right">
                            {formatCurrency(banco.totalMonto)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>

            {/* Gráfico de Distribución por Cajero */}
            {reportData.totalesPorCajero?.length > 0 && (
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Distribución por Cajero
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={prepareChartData()}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) =>
                          `${name}: ${(percent * 100).toFixed(0)}%`
                        }
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {prepareChartData().map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            )}
          </Grid>
        </>
      )}

      {!reportData && !loading && (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="textSecondary">
            Seleccione una fecha y haga clic en "Generar Reporte" para ver los datos
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default ReportDailyPage;
